import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Feature flags for the active store. The resolved key list arrives with the
 * session (`GET /seller/auth/me` → `featureFlags`), so this provider is a thin
 * wrapper that turns that array into a fast membership set and exposes it to the
 * whole dashboard. Mounted high in ProtectedLayout, fed by the session query —
 * no extra request. A flag not in the set (or an unauthenticated/degraded
 * session) reads as OFF, the safe default.
 *
 * Consume via `useFlag("MY_FLAG")` for a single check, or `useFeatureFlags()`
 * for the raw set / predicate.
 */
type FeatureFlagsContextValue = {
  isEnabled: (flagKey: string) => boolean;
  flags: ReadonlySet<string>;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(
  null,
);

export function FeatureFlagsProvider({
  flags,
  children,
}: {
  // The resolved-ON flag keys from the session. Undefined while the session is
  // still loading or on a degraded response — treated as "no flags on".
  flags: string[] | undefined;
  children: ReactNode;
}) {
  const value = useMemo<FeatureFlagsContextValue>(() => {
    const set = new Set(flags ?? []);
    return {
      flags: set,
      isEnabled: (flagKey: string) => set.has(flagKey),
    };
  }, [flags]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
    );
  }
  return ctx;
}

// Convenience for the common single-flag check: `const showX = useFlag("X")`.
export function useFlag(flagKey: string): boolean {
  return useFeatureFlags().isEnabled(flagKey);
}
